/**
 * Learning Center Page
 * Restructured with 6-Phase AI Onboarding Flow
 */
import React, { useState, useEffect, useCallback } from 'react';
import { 
  getConnections, getKnowledgeEntries, createKnowledgeEntry, 
  updateKnowledgeEntry, deleteKnowledgeEntry,
  getPendingSuggestions, reviewSuggestion,
  getOnboardingProgress, getDBAQueries, saveDBAQuery, deleteDBAQuery, updateDBAQuery,
  searchKnowledgeBase, syncKnowledgeFromDBAQuery, markPhaseComplete
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  BookOpen, Plus, Pencil, Trash2, Database, Loader2, Filter, 
  CheckCircle2, Tag, GraduationCap, Shield, Check, X, Table,
  Search, Code, FileCode, Link2, Building2
} from 'lucide-react';

// Import Onboarding Components
import {
  PhaseTracker,
  Phase1BusinessContext,
  Phase2ServerArchitecture,
  Phase3TableDefinitions,
  Phase4Relationships,
  Phase5QueryRules,
  Phase6AILearning,
  TableReview,
  AILearningSummary,
  QueryPatterns
} from '../components/onboarding';
import EnumWizard from '../components/onboarding/EnumWizard';
import BusinessRules from '../components/onboarding/BusinessRules';
import { useNavigate } from 'react-router-dom';

const CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'business_logic', label: 'Business Logic' },
  { value: 'relationship', label: 'Relationships' },
  { value: 'column', label: 'Columns' },
  { value: 'table', label: 'Tables' },
  { value: 'general', label: 'General' }
];

const CATEGORY_COLORS = {
  business_logic: 'bg-purple-100 text-purple-700 border-purple-200',
  relationship: 'bg-blue-100 text-blue-700 border-blue-200',
  column: 'bg-amber-100 text-amber-700 border-amber-200',
  table: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  general: 'bg-gray-100 text-gray-600 border-gray-200'
};

const LearningPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('onboarding');
  
  // Onboarding state
  const [currentPhase, setCurrentPhase] = useState(1);
  const [phaseProgress, setPhaseProgress] = useState({
    1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0
  });
  
  // Knowledge Base state
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Manual entry dialog
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ subject: '', answer: '', category: 'general', question: '' });
  const [savingManual, setSavingManual] = useState(false);
  
  // Edit
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  
  // Admin approval
  const [pendingSuggestions, setPendingSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [reviewingId, setReviewingId] = useState(null);
  const [progressLoading, setProgressLoading] = useState(true);
  
  // DBA Queries state
  const [dbaQueries, setDbaQueries] = useState([]);
  const [dbaDialogOpen, setDbaDialogOpen] = useState(false);
  const [dbaForm, setDbaForm] = useState({ 
    name: '', description: '', sql: '', tags: '', tables_used: '', notes: '' 
  });
  const [savingDba, setSavingDba] = useState(false);
  const [editingDbaId, setEditingDbaId] = useState(null); // Track which DBA query is being edited
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);

  // Load global phase progress from backend (shared across all admins)
  useEffect(() => {
    const loadProgress = async () => {
      try {
        const data = await getOnboardingProgress();
        console.log('[DEBUG] Raw API response:', data);
        console.log('[DEBUG] phase_progress:', data.phase_progress);
        console.log('[DEBUG] Phase 4 value:', data.phase_progress?.[4], 'or', data.phase_progress?.['4']);
        
        // Ensure phase_progress is a valid object
        const progress = data.phase_progress && typeof data.phase_progress === 'object' 
          ? data.phase_progress 
          : { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
        
        console.log('[DEBUG] Setting phaseProgress to:', progress);
        setPhaseProgress(progress);
        setCurrentPhase(data.current_phase || 1);
      } catch (error) {
        console.error('Failed to load onboarding progress:', error);
        // Fallback to localStorage for backwards compatibility
        const savedProgress = localStorage.getItem('onboardingProgress');
        if (savedProgress) {
          try {
            const parsed = JSON.parse(savedProgress);
            setPhaseProgress(parsed.progress || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 });
            setCurrentPhase(parsed.currentPhase || 1);
          } catch (e) {
            // Invalid localStorage data
            setPhaseProgress({ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 });
          }
        }
      } finally {
        setProgressLoading(false);
      }
    };
    loadProgress();
  }, []);

  // Progress is now ALWAYS calculated from database data on the backend
  // No need to save it - just use localStorage for temporary UI state
  const saveProgress = useCallback(async (progress, phase) => {
    // Only save to localStorage for temporary UI state (not to backend)
    // Backend calculates progress from actual data (relationships, definitions, etc.)
    localStorage.setItem('onboardingProgress', JSON.stringify({
      progress,
      currentPhase: phase
    }));
  }, []);

  // Fetch latest progress from backend (shared refresh function)
  const fetchProgress = useCallback(async () => {
    try {
      const data = await getOnboardingProgress();
      const progress = data.phase_progress && typeof data.phase_progress === 'object' 
        ? data.phase_progress 
        : { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
      setPhaseProgress(progress);
      setCurrentPhase(data.current_phase || currentPhase);
    } catch (error) {
      console.error('Failed to fetch progress:', error);
    }
  }, [currentPhase]);

  // Child components can call this to signal they want progress refreshed
  // We DON'T use their calculated value - we fetch fresh from API
  const handlePhaseComplete = useCallback(async (phase, percentage) => {
    // Ignore the percentage passed by child components - they calculate incorrectly
    // Instead, refresh progress from the backend which calculates from actual data
    try {
      const data = await getOnboardingProgress();
      const progress = data.phase_progress && typeof data.phase_progress === 'object' 
        ? data.phase_progress 
        : phaseProgress;
      
      console.log('[DEBUG] Refreshed progress from API:', progress);
      setPhaseProgress(progress);
      
      // Do NOT auto-advance phases here. Components call onComplete on mount
      // when they load existing data, which would cascade phases forward.
      // Users navigate between phases via the PhaseTracker clicks.
    } catch (error) {
      console.error('Failed to refresh progress:', error);
    }
  }, [phaseProgress]);

  const handlePhaseClick = (phase) => {
    setCurrentPhase(phase);
  };

  // Knowledge Base functions
  const fetchEntries = async () => {
    setLoading(true);
    try {
      const data = await getKnowledgeEntries(null, selectedCategory === 'all' ? null : selectedCategory);
      // Handle both array response and {entries: [...]} response
      const entriesData = Array.isArray(data) ? data : (data?.entries || []);
      setEntries(entriesData);
    } catch (error) {
      console.error('Failed to load knowledge entries:', error);
      toast.error('Failed to load knowledge entries');
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'knowledge') {
      fetchEntries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedCategory]);

  const handleSaveManual = async () => {
    if (!manualForm.subject.trim() || !manualForm.answer.trim()) {
      toast.error('Subject and answer are required');
      return;
    }
    setSavingManual(true);
    try {
      await createKnowledgeEntry(manualForm);
      toast.success('Knowledge entry added');
      setManualDialogOpen(false);
      setManualForm({ subject: '', answer: '', category: 'general', question: '' });
      fetchEntries();
    } catch (error) {
      toast.error('Failed to save entry');
    } finally {
      setSavingManual(false);
    }
  };

  const handleUpdateEntry = async (entryId) => {
    if (!editText.trim()) return;
    try {
      await updateKnowledgeEntry(entryId, editText);
      toast.success('Entry updated');
      setEditingId(null);
      setEditText('');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to update entry');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Delete this knowledge entry?')) return;
    try {
      await deleteKnowledgeEntry(entryId);
      toast.success('Entry deleted');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };
  
  // DBA Queries functions
  const fetchDBAQueries = useCallback(async () => {
    try {
      const data = await getDBAQueries();
      setDbaQueries(data.queries || []);
    } catch (error) {
      console.error('Failed to fetch DBA queries:', error);
    }
  }, []);
  
  useEffect(() => {
    if (activeTab === 'dba-queries') {
      fetchDBAQueries();
    }
  }, [activeTab, fetchDBAQueries]);
  
  const handleSaveDBAQuery = async () => {
    if (!dbaForm.name.trim() || !dbaForm.description.trim() || !dbaForm.sql.trim()) {
      toast.error('Name, description, and SQL are required');
      return;
    }
    setSavingDba(true);
    try {
      const queryData = {
        name: dbaForm.name,
        description: dbaForm.description,
        sql: dbaForm.sql,
        tags: dbaForm.tags.split(',').map(t => t.trim()).filter(Boolean),
        tables_used: dbaForm.tables_used.split(',').map(t => t.trim()).filter(Boolean),
        notes: dbaForm.notes
      };
      
      if (editingDbaId) {
        // Update existing query
        await updateDBAQuery(editingDbaId, queryData);
        toast.success('DBA Query updated');
      } else {
        // Create new query
        await saveDBAQuery(queryData);
        toast.success('DBA Query saved');
      }
      
      setDbaDialogOpen(false);
      setDbaForm({ name: '', description: '', sql: '', tags: '', tables_used: '', notes: '' });
      setEditingDbaId(null);
      fetchDBAQueries();
    } catch (error) {
      toast.error(editingDbaId ? 'Failed to update DBA query' : 'Failed to save DBA query');
    } finally {
      setSavingDba(false);
    }
  };
  
  // Open edit dialog with existing query data
  const handleEditDBAQuery = (query) => {
    setDbaForm({
      name: query.name || '',
      description: query.description || '',
      sql: query.sql || '',
      tags: (query.tags || []).join(', '),
      tables_used: (query.tables_used || []).join(', '),
      notes: query.notes || ''
    });
    setEditingDbaId(query.id);
    setDbaDialogOpen(true);
  };
  
  const handleDeleteDBAQuery = async (queryId) => {
    if (!window.confirm('Delete this DBA query?')) return;
    try {
      await deleteDBAQuery(queryId);
      toast.success('DBA Query deleted');
      fetchDBAQueries();
    } catch (error) {
      toast.error('Failed to delete DBA query');
    }
  };
  
  // Knowledge Base Search
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    setSearching(true);
    try {
      const data = await searchKnowledgeBase(searchQuery);
      setSearchResults(data);
    } catch (error) {
      toast.error('Search failed');
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  // Admin approval functions
  const fetchPendingSuggestions = async () => {
    setSuggestionsLoading(true);
    try {
      const data = await getPendingSuggestions();
      setPendingSuggestions(data.suggestions || []);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'approval' && user?.role === 'admin') {
      fetchPendingSuggestions();
    }
  }, [activeTab, user]);

  const handleReviewSuggestion = async (entryId, action) => {
    setReviewingId(entryId);
    try {
      await reviewSuggestion(entryId, action);
      toast.success(`Suggestion ${action}ed`);
      fetchPendingSuggestions();
    } catch (error) {
      toast.error('Failed to review suggestion');
    } finally {
      setReviewingId(null);
    }
  };

  // Render current onboarding phase
  const renderCurrentPhase = () => {
    const isAdmin = user?.role === 'admin';
    
    switch (currentPhase) {
      case 1:
        return <Phase1BusinessContext onComplete={(p) => handlePhaseComplete(1, p)} isAdmin={isAdmin} />;
      case 2:
        return <Phase2ServerArchitecture onComplete={(p) => handlePhaseComplete(2, p)} isAdmin={isAdmin} />;
      case 3:
        return <Phase3TableDefinitions 
          onComplete={(p) => handlePhaseComplete(3, p)} 
          isAdmin={isAdmin} 
          onRelationshipsDiscovered={() => {
            // Refresh progress and auto-navigate to Phase 4
            toast.success('Relationships discovered! Moving to Phase 4 to review.', { duration: 3000 });
            fetchProgress(); // Refresh progress from backend
            setTimeout(() => setCurrentPhase(4), 1500); // Auto-navigate after toast
          }}
        />;
      case 4:
        return <Phase4Relationships onComplete={(p) => handlePhaseComplete(4, p)} isAdmin={isAdmin} />;
      case 5:
        return <Phase5QueryRules onComplete={(p) => handlePhaseComplete(5, p)} isAdmin={isAdmin} />;
      case 6:
        return (
          <div className="space-y-6">
            <Phase6AILearning onComplete={(p) => handlePhaseComplete(6, p)} isAdmin={isAdmin} />
            {/* Enum Definitions as part of AI Learning */}
            <EnumWizard onComplete={() => fetchProgress()} isAdmin={isAdmin} />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="p-6 w-full">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <GraduationCap className="w-7 h-7 text-amber-500" />
          Learning Center
        </h1>
        <p className="text-gray-500 mt-1">
          Teach the AI about your business, databases, and query patterns
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        {/* Main Learning Tabs - Organized into groups */}
        <TabsList className="flex flex-wrap gap-1 mb-6 bg-gray-100/80 p-1 rounded-lg">
          {/* Primary: Onboarding Flow */}
          <TabsTrigger value="onboarding" className="flex items-center gap-2 data-[state=active]:bg-white">
            <GraduationCap className="w-4 h-4" />
            Onboarding
          </TabsTrigger>
          <TabsTrigger value="tables" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Table className="w-4 h-4" />
            Table Review
          </TabsTrigger>
          <TabsTrigger value="patterns" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Filter className="w-4 h-4" />
            Query Patterns
          </TabsTrigger>
          
          {/* Separator */}
          <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
          
          {/* Secondary: Knowledge Management */}
          <TabsTrigger value="knowledge" className="flex items-center gap-2 data-[state=active]:bg-white">
            <BookOpen className="w-4 h-4" />
            Knowledge
          </TabsTrigger>
          <TabsTrigger value="dba-queries" className="flex items-center gap-2 data-[state=active]:bg-white">
            <FileCode className="w-4 h-4" />
            DBA Queries
          </TabsTrigger>
          <TabsTrigger value="business-rules" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Building2 className="w-4 h-4" />
            Business Rules
          </TabsTrigger>
          <TabsTrigger value="search" className="flex items-center gap-2 data-[state=active]:bg-white">
            <Search className="w-4 h-4" />
            Search All
          </TabsTrigger>
          {user?.role === 'admin' && (
            <TabsTrigger value="approval" className="flex items-center gap-2 data-[state=active]:bg-white">
              <Shield className="w-4 h-4" />
              Approval
              {pendingSuggestions.length > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-red-500 text-white rounded-full">
                  {pendingSuggestions.length}
                </span>
              )}
            </TabsTrigger>
          )}
        </TabsList>

        {/* Onboarding Tab */}
        <TabsContent value="onboarding" className="space-y-6">
          {progressLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
              <span className="ml-3 text-gray-600">Loading onboarding progress...</span>
            </div>
          ) : (
            <>
              <PhaseTracker 
                currentPhase={currentPhase}
                phaseProgress={phaseProgress}
                onPhaseClick={handlePhaseClick}
                user={user}
                onMarkComplete={async (phaseId) => {
                  try {
                    await markPhaseComplete(phaseId);
                    toast.success(`Phase ${phaseId} marked as complete!`);
                    // Refresh progress
                    const progress = await getOnboardingProgress();
                    setCurrentPhase(progress.current_phase || 1);
                    setPhaseProgress(progress.phase_progress || {});
                  } catch (error) {
                    toast.error('Failed to mark phase complete');
                  }
                }}
              />
              {renderCurrentPhase()}
              
              {/* AI Learning Summary - appears after all phases */}
              <div className="mt-8">
                <AILearningSummary 
                  phaseProgress={phaseProgress}
                  onStartQuerying={(question) => {
                    // Navigate to AI Query page with optional pre-filled question
                    navigate('/ai-query', { state: { question } });
                  }}
                />
              </div>
            </>
          )}
        </TabsContent>

        {/* Table Review Tab */}
        <TabsContent value="tables" className="space-y-6">
          <TableReview 
            isAdmin={user?.role === 'admin'}
            onRelationshipsDiscovered={() => {
              // Optionally update phase 4 progress
              toast.success('Relationships updated! Check Phase 4.');
            }}
          />
        </TabsContent>

        {/* Query Patterns Tab */}
        <TabsContent value="patterns" className="space-y-6">
          <QueryPatterns 
            onComplete={(progress) => {
              // Query patterns contribute to Phase 5 progress
              console.log('Query patterns progress:', progress);
            }}
          />
        </TabsContent>

        {/* Knowledge Base Tab */}
        <TabsContent value="knowledge" className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-sm text-gray-500">{entries.length} entries</span>
            </div>
            <Button onClick={() => setManualDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Add Entry
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center p-8">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : entries.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {entries.map((entry) => (
                <Card key={entry.id} className="bg-white border-gray-200 hover:shadow-md transition-all">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${CATEGORY_COLORS[entry.category] || CATEGORY_COLORS.general}`}>
                          {entry.category}
                        </span>
                        <span className="text-xs font-medium text-gray-700 flex items-center gap-1">
                          <Tag className="w-3 h-3" />
                          {entry.subject}
                        </span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => { setEditingId(entry.id); setEditText(entry.answer); }}
                          className="p-1 text-gray-400 hover:text-gray-700">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleDeleteEntry(entry.id)}
                          className="p-1 text-gray-400 hover:text-red-600">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    
                    {entry.question && (
                      <p className="text-sm text-gray-500 mb-1 italic">{entry.question}</p>
                    )}
                    
                    {editingId === entry.id ? (
                      <div className="space-y-2">
                        <Textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={3} />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleUpdateEntry(entry.id)}>Save</Button>
                          <Button size="sm" variant="outline" onClick={() => setEditingId(null)}>Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-700">{entry.answer}</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="text-center p-8 bg-gray-50 rounded-lg">
              <Database className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No knowledge entries yet</p>
              <p className="text-sm text-gray-400">Complete the onboarding phases to build your knowledge base</p>
            </div>
          )}
        </TabsContent>

        {/* Admin Approval Tab */}
        {user?.role === 'admin' && (
          <TabsContent value="approval" className="space-y-4">
            <Card className="bg-white border-gray-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-amber-600" />
                  Pending Suggestions ({pendingSuggestions.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {suggestionsLoading ? (
                  <div className="flex items-center justify-center p-8">
                    <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
                  </div>
                ) : pendingSuggestions.length === 0 ? (
                  <div className="text-center p-8">
                    <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-3" />
                    <p className="text-gray-500">No pending suggestions</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingSuggestions.map((suggestion) => (
                      <div key={suggestion.id} className="p-4 border rounded-lg bg-gray-50">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${CATEGORY_COLORS[suggestion.category] || CATEGORY_COLORS.general}`}>
                              {suggestion.category}
                            </span>
                            <p className="text-sm font-medium mt-2">{suggestion.subject}</p>
                            {suggestion.question && (
                              <p className="text-sm text-gray-500 italic">{suggestion.question}</p>
                            )}
                            <p className="text-sm text-gray-700 mt-1">{suggestion.answer}</p>
                          </div>
                          <div className="flex gap-1 ml-4">
                            <Button 
                              size="sm" 
                              onClick={() => handleReviewSuggestion(suggestion.id, 'approve')}
                              disabled={reviewingId === suggestion.id}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={() => handleReviewSuggestion(suggestion.id, 'reject')}
                              disabled={reviewingId === suggestion.id}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
        
        {/* DBA Queries Tab */}
        <TabsContent value="dba-queries">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileCode className="w-5 h-5 text-blue-500" />
                    DBA Expert Queries
                  </CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Add SQL queries written by your DBAs that the AI can learn from and reference
                  </p>
                </div>
                {user?.role === 'admin' && (
                  <Button onClick={() => setDbaDialogOpen(true)} className="bg-blue-500 hover:bg-blue-600">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Query
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {dbaQueries.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileCode className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No DBA queries added yet</p>
                  <p className="text-sm">Add expert SQL queries to help the AI learn</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {dbaQueries.map((query) => (
                    <div key={query.id} className="p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 flex items-center gap-2">
                            <Code className="w-4 h-4 text-blue-500" />
                            {query.name}
                          </h4>
                          <p className="text-sm text-gray-600 mt-1">{query.description}</p>
                          {query.tags?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {query.tags.map((tag, idx) => (
                                <Badge key={idx} variant="secondary" className="text-xs">{tag}</Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        {user?.role === 'admin' && (
                          <div className="flex gap-1">
                            {/* Edit Button */}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => handleEditDBAQuery(query)}
                              className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                              title="Edit this DBA query"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {/* Sync to AI Button */}
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={async () => {
                                try {
                                  toast.info('Syncing to Smart AI...');
                                  const result = await syncKnowledgeFromDBAQuery(query.id);
                                  const updates = result.corrections_made?.length || 0;
                                  toast.success(
                                    <div>
                                      <strong>Synced to Smart AI!</strong>
                                      <br />
                                      <span className="text-sm">{updates} knowledge entries updated</span>
                                    </div>
                                  );
                                } catch (error) {
                                  toast.error('Failed to sync to AI');
                                }
                              }}
                              className="text-green-600 hover:text-green-800 hover:bg-green-50"
                              title="Sync this query to Smart AI knowledge base"
                            >
                              <CheckCircle2 className="w-4 h-4 mr-1" />
                              <span className="text-xs">Sync AI</span>
                            </Button>
                            {/* Delete Button */}
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              onClick={() => handleDeleteDBAQuery(query.id)}
                              className="text-red-500 hover:text-red-700"
                              title="Delete this DBA query"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                      <div className="mt-3">
                        <pre className="text-xs bg-gray-900 text-green-400 p-3 rounded overflow-x-auto">
                          {query.sql}
                        </pre>
                      </div>
                      {query.tables_used?.length > 0 && (
                        <p className="text-xs text-gray-500 mt-2">
                          <strong>Tables:</strong> {query.tables_used.join(', ')}
                        </p>
                      )}
                      {query.notes && (
                        <p className="text-xs text-gray-600 mt-1 italic">
                          <strong>Notes:</strong> {query.notes}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Business Rules Tab */}
        <TabsContent value="business-rules">
          <Card>
            <CardContent className="p-6">
              <BusinessRules user={user} />
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Search Tab */}
        <TabsContent value="search">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <Search className="w-5 h-5 text-purple-500" />
                Search Knowledge Base
              </CardTitle>
              <p className="text-sm text-gray-500">
                Search across table definitions, relationships, enums, DBA queries, and all knowledge entries
              </p>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-6">
                <Input 
                  placeholder="Search for tables, columns, relationships, enums..." 
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  className="flex-1"
                />
                <Button onClick={handleSearch} disabled={searching}>
                  {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                  Search
                </Button>
              </div>
              
              {searchResults && (
                <div className="space-y-6">
                  <p className="text-sm text-gray-600">
                    Found <strong>{searchResults.total_count}</strong> results for "{searchResults.query}"
                  </p>
                  
                  {/* Table Definitions */}
                  {searchResults.results.table_definitions?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-700 flex items-center gap-2 mb-2">
                        <Table className="w-4 h-4" />
                        Table Definitions ({searchResults.results.table_definitions.length})
                      </h4>
                      <div className="space-y-2">
                        {searchResults.results.table_definitions.slice(0, 5).map((td, idx) => (
                          <div key={idx} className="p-2 bg-gray-50 rounded text-sm">
                            <span className="font-mono text-blue-600">{td.table}</span>
                            <span className="text-gray-600 ml-2">- {td.definition?.slice(0, 100)}...</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Relationships */}
                  {searchResults.results.relationships?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-700 flex items-center gap-2 mb-2">
                        <Link2 className="w-4 h-4" />
                        Relationships ({searchResults.results.relationships.length})
                      </h4>
                      <div className="space-y-2">
                        {searchResults.results.relationships.slice(0, 5).map((rel, idx) => (
                          <div key={idx} className="p-2 bg-blue-50 rounded text-sm">
                            <span className="font-mono">{rel.source_table}.{rel.source_column}</span>
                            <span className="mx-2 text-gray-400">→</span>
                            <span className="font-mono">{rel.target_table}.{rel.target_column}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Enum Definitions */}
                  {searchResults.results.enum_definitions?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-700 flex items-center gap-2 mb-2">
                        <Tag className="w-4 h-4" />
                        Enum Definitions ({searchResults.results.enum_definitions.length})
                      </h4>
                      <div className="space-y-2">
                        {searchResults.results.enum_definitions.slice(0, 5).map((en, idx) => (
                          <div key={idx} className="p-2 bg-green-50 rounded text-sm">
                            <span className="font-mono text-green-600">{en.table}.{en.column}</span>
                            <span className="text-gray-600 ml-2">= {en.values?.slice(0, 80)}...</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* DBA Queries */}
                  {searchResults.results.dba_queries?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-700 flex items-center gap-2 mb-2">
                        <FileCode className="w-4 h-4" />
                        DBA Queries ({searchResults.results.dba_queries.length})
                      </h4>
                      <div className="space-y-2">
                        {searchResults.results.dba_queries.slice(0, 5).map((dq, idx) => (
                          <div key={idx} className="p-2 bg-purple-50 rounded text-sm">
                            <span className="font-medium text-purple-700">{dq.name}</span>
                            <span className="text-gray-600 ml-2">- {dq.description?.slice(0, 80)}...</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Knowledge Entries */}
                  {searchResults.results.knowledge_entries?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-700 flex items-center gap-2 mb-2">
                        <BookOpen className="w-4 h-4" />
                        Knowledge Entries ({searchResults.results.knowledge_entries.length})
                      </h4>
                      <div className="space-y-2">
                        {searchResults.results.knowledge_entries.slice(0, 5).map((k, idx) => (
                          <div key={idx} className="p-2 bg-amber-50 rounded text-sm">
                            <span className="font-medium text-amber-700">{k.subject}</span>
                            <span className="text-gray-600 ml-2">- {k.answer?.slice(0, 80)}...</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {searchResults.total_count === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>No results found for "{searchResults.query}"</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Manual Entry Dialog */}
      <Dialog open={manualDialogOpen} onOpenChange={setManualDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Knowledge Entry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Subject</Label>
              <Input 
                value={manualForm.subject} 
                onChange={(e) => setManualForm({...manualForm, subject: e.target.value})}
                placeholder="e.g., users table, deposit flow"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={manualForm.category} onValueChange={(v) => setManualForm({...manualForm, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.filter(c => c.value !== 'all').map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Question (optional)</Label>
              <Input 
                value={manualForm.question} 
                onChange={(e) => setManualForm({...manualForm, question: e.target.value})}
                placeholder="What question does this answer?"
              />
            </div>
            <div>
              <Label>Answer</Label>
              <Textarea 
                value={manualForm.answer} 
                onChange={(e) => setManualForm({...manualForm, answer: e.target.value})}
                placeholder="The knowledge/answer..."
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManualDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveManual} disabled={savingManual}>
              {savingManual ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* DBA Query Dialog - Create/Edit */}
      <Dialog open={dbaDialogOpen} onOpenChange={(open) => {
        setDbaDialogOpen(open);
        if (!open) {
          // Reset form when closing
          setEditingDbaId(null);
          setDbaForm({ name: '', description: '', sql: '', tags: '', tables_used: '', notes: '' });
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileCode className="w-5 h-5 text-blue-500" />
              {editingDbaId ? 'Edit DBA Expert Query' : 'Add DBA Expert Query'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Query Name * <span className="text-xs text-gray-500">(used for AI matching)</span></Label>
              <Input 
                value={dbaForm.name} 
                onChange={(e) => setDbaForm({...dbaForm, name: e.target.value})}
                placeholder="e.g., Daily LoggedIn user, Daily cashsite brand performance"
              />
              <p className="text-xs text-gray-500 mt-1">
                💡 Use descriptive names - AI matches questions to these names
              </p>
            </div>
            <div>
              <Label>Description *</Label>
              <Input 
                value={dbaForm.description} 
                onChange={(e) => setDbaForm({...dbaForm, description: e.target.value})}
                placeholder="What does this query do? e.g., Gets daily login counts per brand"
              />
            </div>
            <div>
              <Label>SQL Query *</Label>
              <Textarea 
                value={dbaForm.sql} 
                onChange={(e) => setDbaForm({...dbaForm, sql: e.target.value})}
                placeholder="SELECT ... FROM ..."
                rows={6}
                className="font-mono text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tags (comma-separated)</Label>
                <Input 
                  value={dbaForm.tags} 
                  onChange={(e) => setDbaForm({...dbaForm, tags: e.target.value})}
                  placeholder="users, daily, report"
                />
              </div>
              <div>
                <Label>Tables Used (comma-separated)</Label>
                <Input 
                  value={dbaForm.tables_used} 
                  onChange={(e) => setDbaForm({...dbaForm, tables_used: e.target.value})}
                  placeholder="Member.member.users, Affiliate.affiliate.deposits"
                />
              </div>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea 
                value={dbaForm.notes} 
                onChange={(e) => setDbaForm({...dbaForm, notes: e.target.value})}
                placeholder="Any additional notes for the AI..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => {
              setDbaDialogOpen(false);
              setEditingDbaId(null);
              setDbaForm({ name: '', description: '', sql: '', tags: '', tables_used: '', notes: '' });
            }}>
              Cancel
            </Button>
            <Button onClick={handleSaveDBAQuery} disabled={savingDba} className="bg-blue-500 hover:bg-blue-600">
              {savingDba ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              {editingDbaId ? 'Update Query' : 'Save Query'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LearningPage;
